<template>
  <div v-if="showWarning" class="bg-warning text-white py-3 px-6 lg:px-20 flex justify-between items-center">
    <div class="flex items-center gap-2 flex-1 justify-center">
      <span class="text-sm">System nie będzie dostępny w sobotę 23.01.2026 w godzinach od 01:00 do 06:00. Przepraszamy
        za utrudnienia.</span>
    </div>
    <button @click="showWarning = false" class="text-white hover:bg-gray-700 rounded-full p-2 transition-colors">
      <i class="pi pi-times text-sm"></i>
    </button>
  </div>

  <div
    class="bg-secondary-700 text-secondary-50 px-4 sm:px-6 lg:px-8 py-3 text-sm flex items-center gap-2 w-full shadow-inner">
    <i class="pi pi-home text-secondary-300"></i>
    <span class="text-secondary-400">/</span>
    <span>Pulpit</span>
  </div>

  <header class="relative bg-white shadow-sm">
    <div class="mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <h1 class="text-3xl font-bold tracking-tight text-gray-900">Pulpit - moduł sprzedażowy</h1>
    </div>
  </header>

  <div class="bg-gray-50 px-4 sm:px-6 py-8 md:px-12 lg:px-20">
    <div class="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <div class="bg-white shadow-sm rounded-lg border border-gray-100 p-6">
        <div class="flex items-center justify-between gap-4 mb-4">
          <h3 class="text-lg font-semibold text-gray-900">Przychody i koszty 2026</h3>
          <span class="text-xs text-gray-500">Dane: styczeń-kwiecień</span>
        </div>
        <Chart type="bar" :data="financeChartData" :options="financeChartOptions" class="h-[20rem]" />

        <div class="mt-5 rounded-lg bg-slate-100 px-4 py-1 text-sm">
          <div class="divide-y divide-white">
            <div class="flex items-center justify-between py-2">
              <span class="font-semibold text-slate-800">Przychód łącznie</span>
              <span class="font-normal text-slate-700">{{ formatCurrency(financeSummary.totalRevenue) }}</span>
            </div>
            <div class="flex items-center justify-between py-2">
              <span class="font-semibold text-slate-800">Koszty łącznie</span>
              <span class="font-normal text-slate-700">{{ formatCurrency(financeSummary.totalCosts) }}</span>
            </div>
            <div class="flex items-center justify-between py-2">
              <span class="font-semibold text-slate-800">Udział kosztów</span>
              <span class="font-normal text-slate-700">{{ formatPercent(financeSummary.costRatio) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white shadow-sm rounded-lg border border-gray-100 p-6">
        <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 class="text-lg font-semibold text-gray-900">Zysk 2026</h3>
          <span class="text-xs text-gray-500">Dane styczeń-kwiecień</span>
        </div>
        <Chart type="line" :data="profitChartData" :options="profitChartOptions" class="mt-4 h-[20rem]" />

        <div class="mt-5 rounded-lg bg-slate-100 px-4 py-1 text-sm">
          <div class="divide-y divide-white">
            <div class="flex items-center justify-between py-2">
              <span class="font-semibold text-slate-800">Wynik łączny</span>
              <span :class="profitSummary.totalProfit >= 0 ? 'text-emerald-700' : 'text-rose-700'" class="font-normal">
                {{ formatCurrency(profitSummary.totalProfit) }}
              </span>
            </div>
            <div class="flex items-center justify-between py-2">
              <span class="font-semibold text-slate-800">Rentowne miesiące</span>
              <span class="font-normal text-slate-700">{{ profitSummary.profitableMonths }}/{{ monthlyResults.length
                }}</span>
            </div>
            <div class="flex items-center justify-between py-2">
              <span class="font-semibold text-slate-800">Najlepszy miesiąc</span>
              <span class="font-normal text-slate-700">{{ profitSummary.bestMonth.month }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white shadow-sm rounded-lg border border-gray-100 p-6">
        <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 class="text-lg font-semibold text-gray-900">Leady 2026</h3>
          <span class="text-xs text-gray-500">Dane styczeń-kwiecień</span>
        </div>
        <Chart type="bar" :data="leadsChartData" :options="leadsChartOptions" class="h-[20rem]" />

        <div class="mt-5 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
          <div class="rounded-lg bg-slate-100 px-4 py-1">
            <div class="divide-y divide-white">
              <div class="flex items-center justify-between py-2">
                <span class="font-semibold text-slate-800">Leady łącznie</span>
                <span class="font-normal text-slate-700">{{ formatNumber(leadKpis.totalLeads) }}</span>
              </div>
              <div class="flex items-center justify-between py-2">
                <span class="font-semibold text-slate-800">Oferty łącznie</span>
                <span class="font-normal text-slate-700">{{ formatNumber(leadKpis.totalOffers) }}</span>
              </div>
              <div class="flex items-center justify-between py-2">
                <span class="font-semibold text-slate-800">Sprzedaż łącznie</span>
                <span class="font-normal text-slate-700">{{ formatNumber(leadKpis.totalSales) }}</span>
              </div>
            </div>
          </div>
          <div class="rounded-lg bg-slate-100 px-4 py-1">
            <div class="divide-y divide-white">
              <div class="flex items-center justify-between py-2">
                <span class="font-semibold text-slate-800">Lead/Oferta</span>
                <span class="font-normal text-slate-700">{{ formatPercent(leadKpis.leadToOfferRate) }}</span>
              </div>
              <div class="flex items-center justify-between py-2">
                <span class="font-semibold text-slate-800">Oferta/Sprzedaż</span>
                <span class="font-normal text-slate-700">{{ formatPercent(leadKpis.offerToSaleRate) }}</span>
              </div>
              <div class="flex items-center justify-between py-2">
                <span class="font-semibold text-slate-800">Lead/Sprzedaż</span>
                <span class="font-normal text-slate-700">{{ formatPercent(leadKpis.leadToSaleRate) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { setStoreValue } from '@pages/Helper/utils.js';
import Chart from 'primevue/chart';

const props = defineProps({
  systemName: String,
  currentModule: String,
  systemData: Object,
});

const showWarning = ref(false);
const monthlyFinance = ref([
  { month: 'Styczeń', revenue: 643404, costs: 690278 },
  { month: 'Luty', revenue: 2624231, costs: 1801225 },
  { month: 'Marzec', revenue: 826178, costs: 1036539 },
  { month: 'Kwiecień', revenue: 1047218, costs: 872200 },
]);

const monthlyLeads = ref([
  { month: 'Styczeń', leads: 8, offers: 3, sales: 1 },
  { month: 'Luty', leads: 87, offers: 36, sales: 14 },
  { month: 'Marzec', leads: 50, offers: 22, sales: 9 },
  { month: 'Kwiecień', leads: 46, offers: 20, sales: 8 },
]);

const currencyFormatter = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
  maximumFractionDigits: 0,
});
const numberFormatter = new Intl.NumberFormat('pl-PL');

const formatCurrency = (value) => currencyFormatter.format(value);
const formatNumber = (value) => numberFormatter.format(value);
const formatPercent = (value) => `${value.toFixed(1)}%`;

const monthlyResults = computed(() => monthlyFinance.value.map((item) => ({
  ...item,
  result: item.revenue - item.costs,
})));

const financeSummary = computed(() => {
  const totalRevenue = monthlyResults.value.reduce((sum, item) => sum + item.revenue, 0);
  const totalCosts = monthlyResults.value.reduce((sum, item) => sum + item.costs, 0);
  const costRatio = totalRevenue ? (totalCosts / totalRevenue) * 100 : 0;

  return {
    totalRevenue,
    totalCosts,
    costRatio,
  };
});

const profitSummary = computed(() => {
  if (!monthlyResults.value.length) {
    return {
      totalProfit: 0,
      profitableMonths: 0,
      bestMonth: { month: '-', result: 0 },
      worstMonth: { month: '-', result: 0 },
    };
  }

  const totalProfit = monthlyResults.value.reduce((sum, item) => sum + item.result, 0);
  const profitableMonths = monthlyResults.value.filter((item) => item.result > 0).length;
  const bestMonth = monthlyResults.value.reduce((best, item) => (item.result > best.result ? item : best), monthlyResults.value[0]);
  const worstMonth = monthlyResults.value.reduce((worst, item) => (item.result < worst.result ? item : worst), monthlyResults.value[0]);

  return {
    totalProfit,
    profitableMonths,
    bestMonth,
    worstMonth,
  };
});

const financeChartData = computed(() => ({
  labels: monthlyResults.value.map((item) => item.month),
  datasets: [
    {
      type: 'bar',
      label: 'Przychód',
      data: monthlyResults.value.map((item) => item.revenue),
      backgroundColor: '#0ea5e9',
      borderRadius: 6,
    },
    {
      type: 'bar',
      label: 'Koszty',
      data: monthlyResults.value.map((item) => item.costs),
      backgroundColor: '#f97316',
      borderRadius: 6,
    },
  ],
}));

const financeChartOptions = computed(() => ({
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        usePointStyle: true,
        boxWidth: 8,
      },
    },
    tooltip: {
      callbacks: {
        label: (context) => `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`,
      },
    },
  },
  scales: {
    y: {
      ticks: {
        callback: (value) => formatCurrency(value),
      },
      grid: {
        color: '#e5e7eb',
      },
    },
    x: {
      grid: {
        display: false,
      },
    },
  },
}));

const profitChartData = computed(() => ({
  labels: monthlyResults.value.map((item) => item.month),
  datasets: [
    {
      type: 'line',
      label: 'Zysk/Strata',
      data: monthlyResults.value.map((item) => item.result),
      borderColor: '#4f46e5',
      backgroundColor: 'rgba(79, 70, 229, 0.18)',
      fill: true,
      tension: 0.3,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: '#4f46e5',
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
    },
  ],
}));

const profitChartOptions = computed(() => ({
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        usePointStyle: true,
        boxWidth: 8,
      },
    },
    tooltip: {
      callbacks: {
        label: (context) => `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`,
      },
    },
  },
  scales: {
    y: {
      ticks: {
        callback: (value) => formatCurrency(value),
      },
      grid: {
        color: '#e5e7eb',
      },
    },
    x: {
      grid: {
        display: false,
      },
    },
  },
}));

const leadsChartData = computed(() => ({
  labels: monthlyLeads.value.map((item) => item.month),
  datasets: [
    {
      type: 'bar',
      label: 'Leady',
      data: monthlyLeads.value.map((item) => item.leads),
      backgroundColor: '#06b6d4',
      stack: 'pipeline',
      borderRadius: 6,
    },
    {
      type: 'bar',
      label: 'Oferty',
      data: monthlyLeads.value.map((item) => item.offers),
      backgroundColor: '#f97316',
      stack: 'pipeline',
      borderRadius: 6,
    },
    {
      type: 'bar',
      label: 'Sprzedaż',
      data: monthlyLeads.value.map((item) => item.sales),
      backgroundColor: '#16a34a',
      stack: 'pipeline',
      borderRadius: 6,
    },
  ],
}));

const leadsChartOptions = computed(() => ({
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        usePointStyle: true,
        boxWidth: 8,
      },
    },
    tooltip: {
      callbacks: {
        label: (context) => `${context.dataset.label}: ${context.parsed.y}`,
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      stacked: true,
      ticks: {
        precision: 0,
      },
      grid: {
        color: '#e5e7eb',
      },
    },
    x: {
      stacked: true,
      grid: {
        display: false,
      },
    },
  },
}));

const leadKpis = computed(() => {
  const totalLeads = monthlyLeads.value.reduce((sum, item) => sum + item.leads, 0);
  const totalOffers = monthlyLeads.value.reduce((sum, item) => sum + item.offers, 0);
  const totalSales = monthlyLeads.value.reduce((sum, item) => sum + item.sales, 0);

  const leadToOfferRate = totalLeads ? (totalOffers / totalLeads) * 100 : 0;
  const offerToSaleRate = totalOffers ? (totalSales / totalOffers) * 100 : 0;
  const leadToSaleRate = totalLeads ? (totalSales / totalLeads) * 100 : 0;

  return {
    totalLeads,
    totalOffers,
    totalSales,
    leadToOfferRate,
    offerToSaleRate,
    leadToSaleRate,
  };
});

setStoreValue('currentModule', props.currentModule);
</script>

<style scoped></style>
